import React, { useEffect } from "react";

import { Grid } from "semantic-ui-react";
import { IntlProvider } from "react-intl";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import Footer from "./components/Footer";
import Header from "./components/Header";
import PrivateRoute from "./components/PrivateRoute";

import Dashboard from "./screens/Dashboard";
import Profiles from "./screens/Profiles";
import Patterns from "./screens/Patterns";
import ManageProfile from "./screens/Dashboard/ManageProfile";
import ManagePattern from "./screens/Patterns/ManagePattern";
import HomePageCustomersmodule from "./screens/HomePageCustomers";
import PatternPage from "./screens/PatternPage";
import CategoryPagemodule from "./screens/CategoryPage";
import PatternsPageModule from "./screens/PatternsList";

import styles from "./App.module.scss";
import { connect } from "react-redux";
import store from "./store";
import { defaultLocale, getMessages } from "./i18n";

import HomePageDesignersUModule from "./screens/HomePageDesigners/HomePageDesigners";
import Page404 from "./screens/Page404";
import Alert from "./components/Alert";

import setAuthToken from "./utils/setAuthToken";
import { loadUser } from "./actions/authActions";

import { localeSelector } from "./utils/selectors";

import Cart from "./screens/Cart";
import Favourites from "./screens/Favourites";
import PaymentScreen from "./screens/Payment";
import PlaceOrder from "./screens/PlaceOrder";
import OrderSummary from "./screens/OrderSummary";
import UserList from "./screens/UserList";
import UserEdit from "./screens/UserEdit";
import OrdersListAdmin from "./screens/OrdersListAdmin";
import Register from "./screens/Register";
import Contact from "./screens/Contact";

import PrivacyPolicy from "./screens/Legal/PrivacyPolicy";
import CreatorTerms from "./screens/Legal/CreatorTerms";
import NewsletterTerms from "./screens/Legal/NewsletterTerms";
import PlatformTerms from "./screens/Legal/PlatformTerms";
import OmnibusInfo from "./screens/Legal/OmnibusInfo/OmnibusInfo";

import PatternStudio from "./screens/PatternStudio";
import PurchaseHistory from "./screens/PurchaseHistory";

// ADMIN
import AdminLayout from "./modules/admin/layout/AdminLayout";
import AdminLogin from "./modules/admin/pages/AdminLogin";
import AdminSettings from "./modules/admin/pages/AdminSettings";
import AdminProtectedRoute from "./modules/admin/components/AdminProtectedRoute";
import AdminStats from "./modules/admin/pages/AdminStats";
import ListPattern from "./modules/admin/pages/ListPattern";

// CREATOR
import CreatorLayout from "./modules/creator/layouts/CreatorLayout";
import CreatorProtectedRoute from "./modules/creator/components/CreatorProtectedRoute";
import CreatorDashboard from "./modules/creator/pages/CreatorDashboard";
import CreatorOrders from "./modules/creator/pages/CreatorOrders";
import CreatorFinance from "./modules/creator/pages/CreatorFinance";
import CreatorAnalytics from "./modules/creator/pages/CreatorAnalytics";
import CreatorPromotions from "./modules/creator/pages/CreatorPromotions";
import CreatorMessages from "./modules/creator/pages/CreatorMessages";
import NewProfile from "./screens/Profile/NewProfile";
import Profile from "./screens/Profile";

if (localStorage.token) {
  setAuthToken(localStorage.token);
}

const App = ({ lang }: any) => {
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith("/admin");
  const isCreatorRoute = location.pathname.startsWith("/creator");

  useEffect(() => {
    store.dispatch(loadUser());
  }, []);

  if (isAdminRoute) {
    return (
      <IntlProvider locale={lang} defaultLocale={defaultLocale} messages={getMessages(lang)}>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />

            <Route
              path="/admin"
              element={
                <AdminProtectedRoute>
                  <AdminLayout />
                </AdminProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/stats" replace />} />
              <Route path="users" element={<UserList />} />
              <Route path="orders" element={<OrdersListAdmin />} />
              <Route path="patterns" element={<ListPattern />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="stats" element={<AdminStats />} />
              <Route path="users/:id/edit" element={<UserEdit />} />
              <Route path="pattern/:id/edit" element={<ManagePattern />} />
            </Route>
          </Routes>
        </IntlProvider>
    );
  }

  if (isCreatorRoute) {
    return (
      <IntlProvider locale={lang} defaultLocale={defaultLocale} messages={getMessages(lang)}>
          <Routes>
            <Route
              path="/creator"
              element={
                <CreatorProtectedRoute>
                  <CreatorLayout />
                </CreatorProtectedRoute>
              }
            >
              <Route
                index
                element={<Navigate to="/creator/dashboard" replace />}
              />
              <Route path="dashboard" element={<CreatorDashboard />} />
              <Route path="orders" element={<CreatorOrders />} />
              <Route path="finance" element={<CreatorFinance />} />
              <Route path="analytics" element={<CreatorAnalytics />} />
              <Route path="messages" element={<CreatorMessages />} />
              <Route path="promotions" element={<CreatorPromotions />} />
              <Route path="settings" element={<div>Settings coming soon</div>} />
            </Route>
          </Routes>
        </IntlProvider>
    );
  }

  return (
      <IntlProvider locale={lang} defaultLocale={defaultLocale} messages={getMessages(lang)}>
        <Grid centered columns={1} className={styles.App}>
          <Alert />
          <Grid.Column>
            <Header />

            <div className={styles.inner}>
              <Routes>
                <Route path="/" element={<HomePageCustomersmodule />} />

                <Route
                  path="/order/:id"
                  element={
                    <PrivateRoute>
                      <OrderSummary />
                    </PrivateRoute>
                  }
                />

                <Route path="/profiles" element={<Profiles />} />
                <Route path="/profile/:id" element={<PrivateRoute><NewProfile /></PrivateRoute>} />
                <Route path="/seller/:id" element={<Profile/>} />
                <Route path="/pattern/:id" element={<PatternPage />} />
                <Route path="/patterns/:handle" element={<Patterns />} />

                <Route
                  path="/designer/home-U"
                  element={<HomePageDesignersUModule />}
                />

                <Route path="/category" element={<CategoryPagemodule />} />
                <Route path="/patterns" element={<PatternsPageModule />} />

                <Route
                  path="/studio"
                  element={
                    <PrivateRoute>
                      <PatternStudio />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/designer/:id"
                  element={
                    <PrivateRoute>
                      <PatternStudio />
                    </PrivateRoute>
                  }
                />

                <Route path="/register" element={<Register />} />
                <Route path="/contact" element={<Contact />} />

                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/creator-terms" element={<CreatorTerms />} />
                <Route path="/newsletter-terms" element={<NewsletterTerms />} />
                <Route path="/platform-terms" element={<PlatformTerms />} />
                <Route path="/omnibus-info" element={<OmnibusInfo />} />

                <Route path="/cart/:id?" element={<Cart />} />
                <Route path="/cart" element={<Cart />} />

                <Route path="/payment" element={<PaymentScreen />} />
                <Route path="/order" element={<PlaceOrder />} />

                <Route
                  path="/purchase-history"
                  element={
                    <PrivateRoute>
                      <PurchaseHistory />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/favourites/:id?"
                  element={
                    <PrivateRoute>
                      <Favourites />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/favourites"
                  element={
                    <PrivateRoute>
                      <Favourites />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/manage-profile"
                  element={
                    <PrivateRoute>
                      <ManageProfile />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/manage-pattern/:id?"
                  element={
                    <PrivateRoute>
                      <ManagePattern />
                    </PrivateRoute>
                  }
                />

                <Route path="*" element={<Page404 />} />
              </Routes>
            </div>
          </Grid.Column>

          <Footer />
        </Grid>
      </IntlProvider>
  );
};

const mapStateToProps = (state: any) => ({
  lang: localeSelector(state),
});

export default connect(mapStateToProps)(App);